export const PERMISSION_SUBJECTS = {
  ADMIN_WHITELIST: 'admin_whitelist',
  ADMIN_USER_ROLES: 'admin_user_roles',
  ADMIN_PASSWORDS: 'admin_passwords',
  STOCK_SYNC: 'stock_sync',
  CUSTOMER_SUPPORT: 'customer_support',
  DEFAULT_ORG_LOCATION: 'default_org_location',
  PAYMENT_SETTINGS: 'payment_settings',
  CREDIT_NOTE_SETTINGS: 'credit_note_settings',
  INVENTORY_THRESHOLDS: 'inventory_thresholds',
  APP_RELEASE: 'app_release',
  PROMOTIONS: 'promotions',
  ORDER: 'order',
  PRODUCTS: 'products',
  INVENTORY: 'inventory',
  LOCATIONS: 'locations',
  DELIVERY: 'delivery',
  CATEGORIES: 'categories',
  DIVISIONS: 'divisions',
  REPORTS: 'reports',
  POS_PROMOTIONS: 'pos_promotions',
  APP_USER_WHITELIST: 'app_user_whitelist',
  APP_USER_PIN: 'app_user_pin',
  DATA_EXPORT: 'data_export',
  BULK_ROLE_ASSIGNMENT: 'bulk_role_assignment',
  BULK_ROLE_CREATION: 'bulk_role_creation',
  CRITICAL_AUDIT: 'critical_audit',
}

/**
 * Administration sidebar tabs. Read on any listed subject shows the tab.
 * Must stay aligned with backend ADMIN_PERMISSION_SCHEMA Administration group.
 * Write does not imply Read. Superuser all.manage grants every subject.
 */

export const ADMINISTRATION_NAV = [
  {
    id: 'user_permissions',
    path: '/user-permissions',
    readSubjects: [
      PERMISSION_SUBJECTS.ADMIN_WHITELIST,
      PERMISSION_SUBJECTS.ADMIN_USER_ROLES,
    ],
  },
  {
    id: 'admin_passwords',
    path: '/administration/admin-passwords',
    readSubjects: [PERMISSION_SUBJECTS.ADMIN_PASSWORDS],
  },
  {
    id: 'stock_sync',
    path: '/administration/stock-sync',
    readSubjects: [PERMISSION_SUBJECTS.STOCK_SYNC],
  },
  {
    id: 'promotion_sync',
    path: '/administration/promotion-sync',
    readSubjects: [PERMISSION_SUBJECTS.STOCK_SYNC],
  },
  {
    id: 'purchasable_status',
    path: '/administration/purchasable-status-sync',
    readSubjects: [PERMISSION_SUBJECTS.STOCK_SYNC],
  },
  {
    id: 'customer_support',
    path: '/administration/customer-support',
    readSubjects: [PERMISSION_SUBJECTS.CUSTOMER_SUPPORT],
  },
  {
    id: 'default_org_location',
    path: '/administration/default-org-location',
    readSubjects: [PERMISSION_SUBJECTS.DEFAULT_ORG_LOCATION],
  },
  {
    id: 'payment_settings',
    path: '/payment-settings',
    readSubjects: [PERMISSION_SUBJECTS.PAYMENT_SETTINGS],
  },
  {
    id: 'releases',
    path: '/releases',
    readSubjects: [PERMISSION_SUBJECTS.APP_RELEASE],
  },
  {
    id: 'credit_settings',
    path: '/global-settings/credit-note',
    readSubjects: [PERMISSION_SUBJECTS.CREDIT_NOTE_SETTINGS],
  },
  {
    id: 'inventory_thresholds',
    path: '/global-settings/inventory',
    readSubjects: [PERMISSION_SUBJECTS.INVENTORY_THRESHOLDS],
  },
  {
    id: 'bulk_role_assignment',
    path: '/administration/bulk-role-assignment',
    readSubjects: [PERMISSION_SUBJECTS.BULK_ROLE_ASSIGNMENT],
  },
  {
    id: 'bulk_role_creation',
    path: '/administration/bulk-role-creation',
    readSubjects: [PERMISSION_SUBJECTS.BULK_ROLE_CREATION],
  },
  {
    id: 'critical_audit',
    path: '/administration/critical-audit',
    readSubjects: [PERMISSION_SUBJECTS.CRITICAL_AUDIT],
  },
]

/**
 * Sidebar path → subject that must have `read` (or manage for promotions).
 * null = available to any signed-in admin (e.g. dashboard, change password).
 */
export const SIDEBAR_PATH_READ_SUBJECT = {
  '/dashboard': null,
  '/products/upload': PERMISSION_SUBJECTS.PRODUCTS,
  '/products/purchasable-status': PERMISSION_SUBJECTS.PRODUCTS,
  '/products/manage': PERMISSION_SUBJECTS.PRODUCTS,
  '/inventory/upload': PERMISSION_SUBJECTS.INVENTORY,
  '/inventory/manage': PERMISSION_SUBJECTS.INVENTORY,
  '/inventory/modify-user': PERMISSION_SUBJECTS.INVENTORY,
  '/inventory-sync/create': PERMISSION_SUBJECTS.INVENTORY,
  '/inventory-sync': PERMISSION_SUBJECTS.INVENTORY,
  '/delivery-times/upload': PERMISSION_SUBJECTS.DELIVERY,
  '/delivery-times/manage': PERMISSION_SUBJECTS.DELIVERY,
  '/store-location-mapping': PERMISSION_SUBJECTS.DELIVERY,
  '/locations/upload': PERMISSION_SUBJECTS.LOCATIONS,
  '/locations/manage': PERMISSION_SUBJECTS.LOCATIONS,
  '/orders': PERMISSION_SUBJECTS.ORDER,
  '/orders/purchase/payment-processing': PERMISSION_SUBJECTS.ORDER,
  '/shops': PERMISSION_SUBJECTS.APP_USER_WHITELIST,
  '/promotions/upload': PERMISSION_SUBJECTS.PROMOTIONS,
  '/promotions/manage': PERMISSION_SUBJECTS.PROMOTIONS,
  '/categories/upload': PERMISSION_SUBJECTS.CATEGORIES,
  '/categories/manage': PERMISSION_SUBJECTS.CATEGORIES,
  '/divisions/upload': PERMISSION_SUBJECTS.DIVISIONS,
  '/divisions/manage': PERMISSION_SUBJECTS.DIVISIONS,
  '/pos-store-promotions/upload': PERMISSION_SUBJECTS.POS_PROMOTIONS,
  '/pos-store-promotions/upcoming': PERMISSION_SUBJECTS.POS_PROMOTIONS,
  '/pos-store-promotions': PERMISSION_SUBJECTS.POS_PROMOTIONS,
  '/pos-store-promotions/report': PERMISSION_SUBJECTS.POS_PROMOTIONS,
  '/reports/sales': PERMISSION_SUBJECTS.REPORTS,
  '/reports/inventory-stock': PERMISSION_SUBJECTS.REPORTS,
}

/**
 * Upload / mutating sidebar paths require module `write` (or promotions `manage`).
 * View/list paths stay read-gated via SIDEBAR_PATH_READ_SUBJECT.
 */
export const SIDEBAR_PATH_WRITE_SUBJECT = {
  '/products/upload': PERMISSION_SUBJECTS.PRODUCTS,
  '/inventory/upload': PERMISSION_SUBJECTS.INVENTORY,
  '/inventory/modify-user': PERMISSION_SUBJECTS.INVENTORY,
  '/inventory-sync/create': PERMISSION_SUBJECTS.INVENTORY,
  '/delivery-times/upload': PERMISSION_SUBJECTS.DELIVERY,
  '/locations/upload': PERMISSION_SUBJECTS.LOCATIONS,
  '/promotions/upload': PERMISSION_SUBJECTS.PROMOTIONS,
  '/categories/upload': PERMISSION_SUBJECTS.CATEGORIES,
  '/divisions/upload': PERMISSION_SUBJECTS.DIVISIONS,
  '/pos-store-promotions/upload': PERMISSION_SUBJECTS.POS_PROMOTIONS,
}

export const ADMIN_ACTIONS = {
  FORCE_COMPLETE_SHIPMENT: 'forceCompleteShipment',
  COMPLETE_ORDER: 'completeOrder',
}

export const ADMIN_ROLE_CODES = {
  SUPERUSER: 'SUPERUSER',
  ADMIN: 'ADMIN',
  PAYMENT_VERIFICATION: 'PAYMENT_VERIFICATION',
  FORCE_COMPLETE_SHIPMENT: 'FORCE_COMPLETE_SHIPMENT',
  SENIOR_CUSTOMER_SUPPORT: 'SENIOR_CUSTOMER_SUPPORT',
  JUNIOR_CUSTOMER_SUPPORT: 'JUNIOR_CUSTOMER_SUPPORT',
  PORTAL_ACCESS_CONTROL: 'PORTAL_ACCESS_CONTROL',
  OPERATIONS_EXECUTIVE: 'OPERATIONS_EXECUTIVE',
  /** Prod custom Ops Executive was seeded as OPERATIONS (not OPERATIONS_EXECUTIVE). */
  OPERATIONS: 'OPERATIONS',
  OPERATIONS_MANAGER: 'OPERATIONS_MANAGER',
}

/** Roles allowed to add catalog products not on the original order during shipment. */
const ADD_PRODUCT_ON_SHIPMENT_ROLE_CODES = [
  ADMIN_ROLE_CODES.OPERATIONS_EXECUTIVE,
  ADMIN_ROLE_CODES.OPERATIONS,
  ADMIN_ROLE_CODES.OPERATIONS_MANAGER,
]

const CS_ONLY_ROLE_CODES = new Set([
  ADMIN_ROLE_CODES.SENIOR_CUSTOMER_SUPPORT,
  ADMIN_ROLE_CODES.JUNIOR_CUSTOMER_SUPPORT,
])

/** Must match backend ADMIN_PERMISSION_SCHEMA requireExplicitGrant flags. */
const EXPLICIT_GRANT_ACTIONS = {
  [PERMISSION_SUBJECTS.ORDER]: new Set([
    ADMIN_ACTIONS.FORCE_COMPLETE_SHIPMENT,
    ADMIN_ACTIONS.COMPLETE_ORDER,
  ]),
}

const ADMINISTRATION_EXPLICIT_SUBJECTS = [
  PERMISSION_SUBJECTS.ADMIN_WHITELIST,
  PERMISSION_SUBJECTS.ADMIN_USER_ROLES,
  PERMISSION_SUBJECTS.ADMIN_PASSWORDS,
  PERMISSION_SUBJECTS.STOCK_SYNC,
  PERMISSION_SUBJECTS.CUSTOMER_SUPPORT,
  PERMISSION_SUBJECTS.DEFAULT_ORG_LOCATION,
  PERMISSION_SUBJECTS.PAYMENT_SETTINGS,
  PERMISSION_SUBJECTS.APP_RELEASE,
  PERMISSION_SUBJECTS.CREDIT_NOTE_SETTINGS,
  PERMISSION_SUBJECTS.INVENTORY_THRESHOLDS,
]

for (const subject of ADMINISTRATION_EXPLICIT_SUBJECTS) {
  EXPLICIT_GRANT_ACTIONS[subject] = new Set(['read', 'write'])
}

// Bulk RBAC + Critical Audit are Administration-style explicit grants
EXPLICIT_GRANT_ACTIONS[PERMISSION_SUBJECTS.BULK_ROLE_ASSIGNMENT] = new Set([
  'read',
  'write',
])
EXPLICIT_GRANT_ACTIONS[PERMISSION_SUBJECTS.BULK_ROLE_CREATION] = new Set([
  'read',
  'write',
])
EXPLICIT_GRANT_ACTIONS[PERMISSION_SUBJECTS.CRITICAL_AUDIT] = new Set(['read'])

export function isExplicitGrantAdminPermission(subject, action) {
  return !!EXPLICIT_GRANT_ACTIONS[subject]?.has(action)
}

export function hasAdminRole(roleCodes, roleCode) {
  return Array.isArray(roleCodes) && roleCodes.includes(roleCode)
}

/**
 * Add catalog products onto a shipment (SKUs not on the original order).
 * Allowed for Operations Executive / Manager (incl. legacy OPERATIONS) and Superuser.
 */
export function canAddProductOnShipment(adminRoleCodes, isSuperuserActor) {
  if (isSuperuserActor) return true
  if (!Array.isArray(adminRoleCodes) || adminRoleCodes.length === 0) return false
  return ADD_PRODUCT_ON_SHIPMENT_ROLE_CODES.some((code) =>
    adminRoleCodes.includes(code),
  )
}

/** App Lock PIN regeneration (admin shop details) — Superuser or Senior Customer Support. */
export function canRegenerateShopAppLockPin(isSuperuserActor, adminRoleCodes) {
  if (isSuperuserActor) return true
  return hasAdminRole(adminRoleCodes, ADMIN_ROLE_CODES.SENIOR_CUSTOMER_SUPPORT)
}

/**
 * Mirrors backend hasAdminPermission.
 * Superuser `all.manage` grants every subject/action.
 */
export function hasAdminPermission(merged, subject, action) {
  if (!merged || typeof merged !== 'object') return false
  if (merged.all?.manage) return true
  if (isExplicitGrantAdminPermission(subject, action)) {
    return !!merged[subject]?.[action]
  }
  const s = merged[subject]
  if (!s) return false
  if (s.manage) return true
  return !!s[action]
}

/** True when the actor may open User Permissions (mirrors canManagePortalAccess). */
export function canManagePortalAccess(merged) {
  return (
    hasAdminPermission(merged, PERMISSION_SUBJECTS.ADMIN_USER_ROLES, 'read') ||
    hasAdminPermission(merged, PERMISSION_SUBJECTS.ADMIN_WHITELIST, 'read')
  )
}

export function hasAnyAdminPermission(merged, subjects, action) {
  return (subjects || []).some((subject) =>
    hasAdminPermission(merged, subject, action),
  )
}

export function canReadAdministrationItem(adminPermissions, item) {
  return hasAnyAdminPermission(adminPermissions, item?.readSubjects, 'read')
}

export function canReadSidebarPath(adminPermissions, path) {
  if (!path) return true
  if (adminPermissions?.all?.manage) return true
  const adminSubjects = administrationReadSubjectsForPath(path)
  if (adminSubjects) {
    return hasAnyAdminPermission(adminPermissions, adminSubjects, 'read')
  }
  const writeSubject = SIDEBAR_PATH_WRITE_SUBJECT[path]
  if (writeSubject) {
    if (writeSubject === PERMISSION_SUBJECTS.PROMOTIONS) {
      return hasAdminPermission(adminPermissions, writeSubject, 'manage')
    }
    return hasAdminPermission(adminPermissions, writeSubject, 'write')
  }
  const subject = SIDEBAR_PATH_READ_SUBJECT[path]
  if (subject == null && !(path in SIDEBAR_PATH_READ_SUBJECT)) {
    // Unknown path — keep visible for signed-in admins
    return true
  }
  if (subject == null) return true
  if (subject === PERMISSION_SUBJECTS.PROMOTIONS) {
    return hasAdminPermission(adminPermissions, subject, 'manage')
  }
  return hasAdminPermission(adminPermissions, subject, 'read')
}

/** Excel/CSV download affordances. */
export function canExportData(adminPermissions) {
  return hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.DATA_EXPORT,
    'read',
  )
}

/** Read subjects required for a pathname, or null when the path is not an Administration route. */
export function administrationReadSubjectsForPath(pathname) {
  if (pathname === '/releases' || pathname.startsWith('/releases/')) {
    return [PERMISSION_SUBJECTS.APP_RELEASE]
  }
  const item = ADMINISTRATION_NAV.find((nav) => nav.path === pathname)
  return item ? item.readSubjects : null
}

const MODULE_SUBJECT_ACTIONS = [
  [PERMISSION_SUBJECTS.PRODUCTS, 'read'],
  [PERMISSION_SUBJECTS.INVENTORY, 'read'],
  [PERMISSION_SUBJECTS.LOCATIONS, 'read'],
  [PERMISSION_SUBJECTS.DELIVERY, 'read'],
  [PERMISSION_SUBJECTS.CATEGORIES, 'read'],
  [PERMISSION_SUBJECTS.DIVISIONS, 'read'],
  [PERMISSION_SUBJECTS.REPORTS, 'read'],
  [PERMISSION_SUBJECTS.POS_PROMOTIONS, 'read'],
  [PERMISSION_SUBJECTS.ORDER, 'read'],
  [PERMISSION_SUBJECTS.ORDER, 'write'],
  [PERMISSION_SUBJECTS.ORDER, 'startProcurement'],
  [PERMISSION_SUBJECTS.ORDER, 'create'],
  [PERMISSION_SUBJECTS.APP_USER_WHITELIST, 'read'],
  [PERMISSION_SUBJECTS.APP_USER_WHITELIST, 'write'],
  [PERMISSION_SUBJECTS.APP_USER_PIN, 'read'],
  [PERMISSION_SUBJECTS.APP_USER_PIN, 'write'],
]

function hasAnyModuleOrFullPortalGrant(adminPermissions) {
  for (const [subject, action] of MODULE_SUBJECT_ACTIONS) {
    if (hasAdminPermission(adminPermissions, subject, action)) return true
  }
  if (hasAdminPermission(adminPermissions, PERMISSION_SUBJECTS.PROMOTIONS, 'manage')) {
    return true
  }
  return false
}

function hasNonCsAdministrationRead(adminPermissions) {
  for (const item of ADMINISTRATION_NAV) {
    if (item.id === 'customer_support') continue
    if (canReadAdministrationItem(adminPermissions, item)) return true
  }
  return false
}

/**
 * True when the actor should only see Customer Support.
 * Prefer system CS role codes; permission fallback for custom CS-only maps.
 */
export function isCustomerSupportOnlyActor(
  isSuperuserActor,
  adminPermissions,
  adminRoleCodes,
) {
  if (isSuperuserActor) return false
  if (!adminPermissions || typeof adminPermissions !== 'object') return false
  if (adminPermissions.all?.manage) return false

  if (Array.isArray(adminRoleCodes) && adminRoleCodes.length > 0) {
    const onlyCsRoles = adminRoleCodes.every((code) => CS_ONLY_ROLE_CODES.has(code))
    if (onlyCsRoles) {
      // Senior CS with Create Shop (whitelist) must leave the CS-only shell
      if (hasAnyModuleOrFullPortalGrant(adminPermissions)) return false
      return true
    }
    // Mixed roles (e.g. PAC + CS) are never CS-only
    if (adminRoleCodes.some((code) => CS_ONLY_ROLE_CODES.has(code))) {
      return false
    }
  }

  if (
    !hasAdminPermission(
      adminPermissions,
      PERMISSION_SUBJECTS.CUSTOMER_SUPPORT,
      'read',
    )
  ) {
    return false
  }

  if (hasNonCsAdministrationRead(adminPermissions)) return false
  if (hasAnyModuleOrFullPortalGrant(adminPermissions)) return false

  return true
}

/**
 * True only when promotions is the sole meaningful grant (not when combined
 * with PAC, orders, modules, etc.).
 */
export function isPromotionsOnlyActor(isSuperuserActor, adminPermissions) {
  if (isSuperuserActor) return false
  if (!adminPermissions || typeof adminPermissions !== 'object') return false
  if (adminPermissions.all?.manage) return false
  if (
    !hasAdminPermission(adminPermissions, PERMISSION_SUBJECTS.PROMOTIONS, 'manage')
  ) {
    return false
  }
  if (hasNonCsAdministrationRead(adminPermissions)) return false
  if (
    hasAdminPermission(
      adminPermissions,
      PERMISSION_SUBJECTS.CUSTOMER_SUPPORT,
      'read',
    )
  ) {
    return false
  }
  for (const [subject, action] of MODULE_SUBJECT_ACTIONS) {
    if (hasAdminPermission(adminPermissions, subject, action)) return false
  }
  return true
}

/** Paths a customer-support-only actor may open. */
export function isCustomerSupportOnlyAllowedPath(pathname) {
  const path = String(pathname || '')
  if (path === '/administration/customer-support') return true
  if (path.startsWith('/administration/customer-support/')) return true
  if (path === '/account/change-password') return true
  return false
}

/** Human-readable summary lines for a permission map (no subject keys). */
export function summarizePermissionsForHumans(permissionSchema, permissions) {
  if (permissions?.all?.manage) {
    return ['Full access to everything in the admin portal']
  }
  const lines = []
  for (const subjectSchema of permissionSchema || []) {
    const selected = permissions?.[subjectSchema.subject]
    if (!selected || typeof selected !== 'object') continue
    const active = (subjectSchema.actions || []).filter((a) => selected[a.action])
    if (!active.length) continue
    const actionLabels = active.map((a) => a.label || a.action).join(', ')
    lines.push(`${subjectSchema.label}: ${actionLabels}`)
  }
  return lines.length ? lines : ['No specific permissions selected']
}

/** Portal page labels unlocked by the current selection. */
export function pagesUnlockedByPermissions(permissionSchema, permissions) {
  if (permissions?.all?.manage) {
    return ['All portal pages']
  }
  const labels = new Set()
  for (const subjectSchema of permissionSchema || []) {
    const selected = permissions?.[subjectSchema.subject]
    if (!selected || typeof selected !== 'object') continue
    const hasAny = Object.values(selected).some(Boolean)
    if (!hasAny) continue
    for (const page of subjectSchema.portalPages || []) {
      if (page?.label) labels.add(page.label)
    }
  }
  return [...labels]
}
